import _ from 'lodash';
import log from '@/helpers/log';
import { ops } from './constants';

const DEFAULT_TIME_OFFSET = 24 * 60 * 60 * 1000; // 1 day.

export function getBlankMetric(overrides = {}) {
  return {
    op: overrides.op || '',
    property: overrides.property || null,
    filter: overrides.filter || null,
    expression: overrides.expression || null,
    label: overrides.label || null,
  };
}

export function getBlankKpi() {
  return {
    $id: null,
    name: 'New KPI',
    pivot: null,
    measurements: [getBlankMetric()],
    grouping: null,
    scope: null,
    binSize: null,
    options: {
      limitTo: 'all',
      limitCount: undefined,
    },
    displayOptions: {
      type: null,
      sxsType: null,
      sort: { by: 'metric', order: 'desc' },
      hideBarGaps: false,
      hideLineMarkers: false,
      yAxis: { min: null, max: null },
      boundaries: { middle: 70, top: 90 },
      positiveStatus: true,
      valueBounds: { min: null, max: null },
      titles: { x: null, y: null },
      units: null,
    },
    custom: null,
    state: {},
    monitored: false,
    monitor: {
      threshold: { op: '', value: null },
      periodSize: null,
    },
    sxs: {
      type: null,
      numberOfUnits: null,
      unit: null,
    },
    time: { offset: DEFAULT_TIME_OFFSET },
  };
}

export function getFullOp(op) {
  return ops.find((o) => o.value === op);
}

function getFilterById(id, filters) {
  let filterId = id;
  if (!id || typeof id !== 'string') {
    return null;
  }

  if (id.indexOf('/savedSearches/') > -1) {
    filterId = id.replace('/savedSearches/', '');
  }

  let filter = null;
  filters.forEach((fil) => {
    if (fil.id === filterId) {
      filter = fil;
    }
  });
  return filter;
}

function getPropertyByName(name, properties) {
  if (!name || typeof name !== 'string') {
    return name;
  }

  let property = null;
  properties.forEach((prop) => {
    if (prop.name === name) {
      property = prop;
    }
  });
  return property;
}

export function saveObjectToKpi(k, filters, properties) {
  const newKpi = getBlankKpi();

  if (!filters || !properties) {
    log.error('Filters and properties must be passed in to convert KPIs');
    return newKpi;
  }

  if (!k) {
    log.error('No KPI was passed in');
    return null;
  }

  const { kpi } = k;

  if (k.$id) {
    newKpi.$id = k.$id;
  }

  if (k.name) {
    newKpi.name = k.name;
  }

  if (kpi.binSize) {
    newKpi.binSize = kpi.binSize;
  }

  if (kpi.measurements && Array.isArray(kpi.measurements)) {
    newKpi.measurements = kpi.measurements.map(({
      op,
      filter,
      property,
      expression,
      label,
    }) => {
      const measurement = getBlankMetric({
        op,
        filter,
        property,
        expression,
        label: kpi.measurements.length > 1 ? label : null,
      });

      measurement.property = getPropertyByName(measurement.property, properties);
      measurement.filter = getFilterById(measurement.filter, filters);

      return measurement;
    });
  }

  if (kpi.grouping && kpi.grouping[0]) {
    newKpi.grouping = getPropertyByName(kpi.grouping[0], properties);
  }

  if (kpi.filters && kpi.filters[0]) {
    const [filter] = kpi.filters;
    if (typeof filter === 'string') {
      newKpi.scope = getFilterById(filter, filters);
    } else if (filter.type === 'ref') {
      newKpi.scope = getFilterById(filter.id, filters);
    } else if (filter.type === 'embed') {
      newKpi.scope = { expression: filter.expression };
    }
  }

  if (kpi.options) {
    // Clone the object, otherwise we retain the reference to the vuex object
    newKpi.options = _.clone(kpi.options);
  }

  if (kpi.displayOptions) {
    newKpi.displayOptions = _.clone({
      ...getBlankKpi().displayOptions,
      ...kpi.displayOptions,
    });
  }

  if (kpi.custom) {
    newKpi.custom = kpi.custom;
  }
  if (kpi.sxs && kpi.sxs.duration) {
    const { uiUnit, uiNumberOfUnits } = kpi.sxs;
    newKpi.sxs = {
      type: 'duration',
      join: 'group',
      duration: kpi.sxs.duration,
      unit: uiUnit,
      numberOfUnits: uiNumberOfUnits,
    };

    if (!uiUnit || !uiNumberOfUnits) {
      newKpi.sxs.unit = kpi.sxs.duration;
      newKpi.sxs.numberOfUnits = 1;
    }
  }

  if (kpi.state) {
    newKpi.state = _.clone(kpi.state);
  }

  if (kpi.monitored) {
    newKpi.monitored = kpi.monitored;
    newKpi.monitor = _.clone(kpi.monitor);
  }

  // Set default boundaries on the KPIs if none have been previously saved
  if (!newKpi.displayOptions.boundaries) {
    const { displayOptions: defaults } = getBlankKpi();
    newKpi.displayOptions.boundaries = defaults.boundaries;
    newKpi.displayOptions.positiveStatus = defaults.positiveStatus;
  }

  if (kpi.time && kpi.time.offset) {
    newKpi.time = { offset: kpi.time.offset };
  } else {
    newKpi.time = { offset: DEFAULT_TIME_OFFSET };
  }

  if (kpi.pivot) {
    newKpi.pivot = kpi.pivot;
  }

  return newKpi;
}

export function kpiToSaveObject(kpi) {
  const kpiSaveObject = {
    measurements: [],
    grouping: [],
    filters: [], // either string (legacy), { type: 'ref|embed', (id|expression): '' }
    binSize: null,
    options: {},
    custom: null,
    time: { offset: DEFAULT_TIME_OFFSET },
  };

  if (kpi.binSize) {
    kpiSaveObject.binSize = kpi.binSize;
  }

  if (kpi.measurements && Array.isArray(kpi.measurements)) {
    kpiSaveObject.measurements = kpi.measurements.map((m) => {
      const measurement = {};
      const op = getFullOp(m.op);
      if (!op) return null;

      if (m.label) {
        measurement.label = m.label;
      }

      measurement.op = op.value;
      if (m.expression) {
        measurement.expression = m.expression;
      }
      if (m.filter) {
        const id = typeof m.filter === 'object' ? m.filter.id : m.filter;
        measurement.filter = id;
      }
      if (m.property) {
        const name = typeof m.property === 'object' ? m.property.name : m.property;
        measurement.property = name;
      }
      return measurement;
    }).filter((m) => m !== null);
  }

  // Add grouping if required
  if (kpi.grouping) {
    kpiSaveObject.grouping[0] = typeof kpi.grouping === 'object' ? kpi.grouping.name : kpi.grouping;
  }

  // Add scoping if required
  if (kpi.scope) {
    let filter;
    if (typeof kpi.scope !== 'object') { // migrate from legacy
      filter = { type: 'ref', id: kpi.scope };
    } else if (kpi.scope.id) { // prefer reference as implied by id
      filter = { type: 'ref', id: kpi.scope.id };
    } else if (kpi.scope.expression) {
      filter = { type: 'embed', expression: kpi.scope.expression };
    }
    kpiSaveObject.filters[0] = filter;
  }

  if (kpi.options) {
    kpiSaveObject.options = kpi.options;
  }

  if (kpi.custom) {
    kpiSaveObject.custom = kpi.custom;
  }
  const { unit, numberOfUnits, duration } = kpi.sxs || {};
  if (kpi.sxs && (duration || (unit && numberOfUnits))) {
    kpiSaveObject.sxs = {
      type: 'duration',
      join: 'group',
      duration: kpi.sxs.duration,
      uiUnit: unit,
      uiNumberOfUnits: numberOfUnits,
    };

    // These get restored in the UI, so should be safe to use to always restore duration
    if (typeof unit === 'number' && typeof numberOfUnits === 'number') {
      kpiSaveObject.sxs.duration = unit * numberOfUnits;
    }
  } else {
    kpiSaveObject.sxs = null;
  }

  if (kpi.displayOptions) {
    kpiSaveObject.displayOptions = kpi.displayOptions;
  }

  if (kpi.state) {
    kpiSaveObject.state = kpi.state;
  }

  if (kpi.monitored) {
    kpiSaveObject.monitored = kpi.monitored;
    kpiSaveObject.monitor = kpi.monitor;
  }

  if (kpi.time && kpi.time.offset) {
    kpiSaveObject.time = { offset: kpi.time.offset };
  }

  if (kpi.time && kpi.time.period) {
    kpiSaveObject.time = { period: kpi.time.period };
  }

  if (kpi.pivot) {
    kpiSaveObject.pivot = kpi.pivot;
  }

  const exportObject = {
    name: kpi.name,
    kpi: kpiSaveObject,
  };

  if (kpi.$id) {
    exportObject.$id = kpi.$id;
  }

  return exportObject;
}
